import React, { useEffect, useRef } from "react";
import useSectionScroll from "../../hooks/useSectionScroll";

function useReveal() {
  const ref = useRef();

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          observer.unobserve(el);
        }
      },
      {
        threshold: 0.08,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return ref;
}

export default function CTABanner() {
  const ref = useReveal();
  const goToSection = useSectionScroll();

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        bg-[#1A1714]
        py-12
        sm:py-16
        lg:py-28
      "
    >
      {/* Background Glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="
            absolute
            left-1/2
            top-0
            h-64
            w-64
            -translate-x-1/2
            rounded-full
            bg-[#C8762A]/10
            blur-[90px]

            sm:h-[350px]
            sm:w-[350px]
            sm:blur-[120px]

            lg:h-[450px]
            lg:w-[450px]
            lg:blur-[140px]
          "
        />

        <div
          className="
            absolute
            -bottom-20
            -right-20
            h-48
            w-48
            rounded-full
            bg-[#C8762A]/5
            blur-[80px]

            sm:bottom-0
            sm:right-0
            sm:h-64
            sm:w-64
            sm:blur-[100px]

            lg:h-72
            lg:w-72
            lg:blur-[120px]
          "
        />
      </div>

      {/* Main Content */}
      <div
        ref={ref}
        className="
          relative
          mx-auto
          w-full
          max-w-5xl
          translate-y-8
          px-4
          text-center
          opacity-0
          transition-all
          duration-700

          sm:px-6

          lg:px-8
        "
      >
        {/* Badge */}
        <span
          className="
            inline-flex
            items-center
            justify-center
            rounded-full
            border
            border-[#C8762A]/30
            bg-[#C8762A]/10
            px-3
            py-1.5
            text-[9px]
            font-semibold
            uppercase
            tracking-[0.18em]
            text-[#C8762A]

            sm:px-4
            sm:text-xs
            sm:tracking-[0.22em]
          "
        >
          Let's Build Together
        </span>

        {/* Heading */}
        <h2
          className="
            mx-auto
            mt-5
            max-w-[350px]
            text-[28px]
            font-bold
            leading-[1.15]
            text-white

            sm:mt-6
            sm:max-w-2xl
            sm:text-4xl

            lg:text-5xl
          "
        >
          Have an Idea?
          <br />

          <span className="text-[#C8762A]">
            Let's Turn It Into Reality.
          </span>
        </h2>

        {/* Description */}
        <p
          className="
            mx-auto
            mt-4
            max-w-[350px]
            text-sm
            leading-6
            text-stone-300

            sm:mt-6
            sm:max-w-2xl
            sm:text-base
            sm:leading-8
          "
        >
          We're passionate about helping startups and businesses build modern
          websites, web applications, and scalable software solutions with the
          latest technologies.
        </p>

        {/* Buttons */}
        <div
          className="
            mx-auto
            mt-7
            flex
            w-full
            max-w-sm
            flex-col
            items-stretch
            justify-center
            gap-3

            sm:mt-10
            sm:max-w-none
            sm:flex-row
            sm:items-center
            sm:gap-4
          "
        >
          {/* Start Project */}
          <a
            href="https://wa.me/916376402379?text=Hi%20ZevixTech,%20I%20want%20to%20start%20a%20project."
            target="_blank"
            rel="noopener noreferrer"
            className="
              inline-flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-full
              bg-[#C8762A]
              px-6
              py-3.5
              text-sm
              font-semibold
              text-white
              transition-all
              duration-300

              sm:w-auto
              sm:px-8
              sm:py-4
              sm:text-base

              lg:hover:-translate-y-1
              lg:hover:bg-[#b86b23]
              lg:hover:shadow-lg
              lg:hover:shadow-[#C8762A]/30
            "
          >
            Start Your Project

            <span className="material-symbols-outlined text-lg sm:text-xl">
              arrow_forward
            </span>
          </a>

          {/* View Work */}
          <button
            type="button"
            onClick={() => goToSection("portfolio")}
            className="
              inline-flex
              w-full
              items-center
              justify-center
              rounded-full
              border
              border-white/20
              px-6
              py-3.5
              text-sm
              font-semibold
              text-white
              transition-all
              duration-300

              sm:w-auto
              sm:px-8
              sm:py-4
              sm:text-base

              lg:hover:border-[#C8762A]
              lg:hover:text-[#C8762A]
            "
          >
            View Our Work
          </button>
        </div>
      </div>
    </section>
  );
}